/**
 * Per-channel health rule sets, ported from the inline JS in the real
 * app.py (data-continuiti) — this logic was already JavaScript in the
 * source tool, so the port is close to verbatim.
 */

export type CheckStatus = 'good' | 'warn' | 'miss' | 'info';
export type CheckResult = [CheckStatus, string];

export interface ChannelHealthContext {
  cat: (category: string) => string[];
  has: (pattern: RegExp) => boolean;
  host: (pattern: RegExp) => boolean;
  insight: (pattern: RegExp) => boolean;
}

export interface ChannelCheck {
  label: string;
  types?: string[];
  evaluate: (ctx: ChannelHealthContext) => CheckResult;
}

export interface Channel {
  id: string;
  name: string;
  blurb: string;
  types?: string[];
  limited?: boolean;
  checks: ChannelCheck[];
}

const good = (detail: string): CheckResult => ['good', detail];
const warn = (detail: string): CheckResult => ['warn', detail];
const miss = (detail: string): CheckResult => ['miss', detail];
const info = (detail: string): CheckResult => ['info', detail];

export const ECOM_TYPES = ['ecomm', 'ecomm_sub', 'ecomm_store', 'hybrid'];

export const CHANNELS: Channel[] = [
  {
    id: 'search',
    name: 'Search',
    blurb: 'Paid Search · AI SEO · Shoppable Media',
    checks: [
      {
        label: 'Google Ads conversion tracking',
        evaluate: c =>
          c.has(/google ads conversion/)
            ? good('Conversion pixel detected — Google can be graded and can bid on outcomes.')
            : miss('No Google Ads conversion tag found — paid search would be flying blind.')
      },
      {
        label: 'Remarketing audiences',
        evaluate: c =>
          c.has(/remarketing/)
            ? good('Remarketing tag present — audiences are being built for RLSA/display.')
            : warn(
                "No remarketing tag — high-intent visitors aren't being pooled for re-engagement."
              )
      },
      {
        label: 'Shopping / feed signals (Shoppable Media)',
        types: ECOM_TYPES,
        evaluate: c =>
          c.has(/pmax|merchant/)
            ? good('Merchant/PMax signals present — the product feed is wired into search.')
            : info(
                'No feed hints observed — if this client sells products, verify Merchant Center during onboarding.'
              )
      },
      {
        label: 'Consent signals reaching Google',
        evaluate: c =>
          c.has(/google consent mode/)
            ? good('Consent Mode observed (gcd) — modeled conversions and EEA features stay on.')
            : warn('No Consent Mode signals — Google measurement degrades where consent applies.')
      },
      {
        label: 'Signal durability (server-side)',
        evaluate: c =>
          c.cat('Server-Side Tagging Signals').length
            ? good('Server-side collection hardens Smart Bidding inputs.')
            : warn('Browser-only collection — bidding signals exposed to loss.')
      },
      {
        label: 'First-party data (Enhanced Conversions)',
        evaluate: c =>
          c.has(/enhanced conversions/)
            ? good(
                'Hashed user data observed on Google hits — match rates are being defended with 1P data.'
              )
            : warn('No Enhanced Conversions signals — Google is matching on cookies alone.')
      },
      {
        label: 'SEO',
        evaluate: () =>
          info(
            "Organic health isn't visible in a tag scan — needs a crawl + Search Console access (see Access to request)."
          )
      }
    ]
  },
  {
    id: 'social',
    name: 'Social',
    blurb: 'Meta · TikTok · emerging platforms',
    checks: [
      {
        label: 'Meta measurement',
        evaluate: c => {
          if (!c.has(/meta \(facebook\) pixel/)) {
            return miss('No Meta Pixel — the largest social channel has no feedback loop.');
          }
          return c.insight(/meta pixel without visible conversions api/)
            ? warn(
                'Pixel present but no CAPI signals — event match quality is leaking to ad blockers/ITP.'
              )
            : good('Meta Pixel present with no CAPI red flags raised.');
        }
      },
      {
        label: 'TikTok measurement',
        evaluate: c =>
          c.has(/tiktok/)
            ? good('TikTok Pixel detected.')
            : info("No TikTok Pixel — fine if the channel isn't active; a to-do if it is.")
      },
      {
        label: 'Emerging platform coverage',
        evaluate: c => {
          const n = [
            'pinterest',
            'snapchat',
            'reddit',
            'x / twitter',
            'linkedin',
            'nextdoor',
            'quora'
          ].filter(p => c.has(new RegExp(p)));
          return n.length
            ? good(`Also detected: ${n.join(', ')}.`)
            : info('No secondary social pixels — expansion channels start from zero data.');
        }
      },
      {
        label: 'Pixels confirmed firing at runtime',
        evaluate: c =>
          c.cat('Runtime-Confirmed Pixel Fires').length
            ? good(
                `Observed on the wire during the scan: ${c.cat('Runtime-Confirmed Pixel Fires').slice(0, 4).join(', ')}.`
              )
            : info(
                'No runtime fires captured on scanned pages — verify event coverage at checkout/lead paths.'
              )
      },
      {
        label: 'Signal durability (server-side)',
        evaluate: c =>
          c.cat('Server-Side Tagging Signals').length
            ? good('Server-side collection protects social conversion signals.')
            : warn('No sST — social attribution erodes as browser signal loss grows.')
      },
      {
        label: 'First-party data (Advanced Matching)',
        evaluate: c =>
          c.has(/advanced matching/)
            ? good(
                'Hashed user data observed on Meta hits — event match quality is being defended with 1P data.'
              )
            : warn('No Advanced Matching signals — Meta match quality rides on the pixel alone.')
      }
    ]
  },
  {
    id: 'tvaudio',
    name: 'TV, Audio & Display',
    blurb: 'Streaming · Linear · Online Video · Audio · Display & OOH',
    checks: [
      {
        label: 'Programmatic / DSP footprint',
        evaluate: c =>
          c.cat('DSP / Programmatic').length
            ? good(`Detected: ${c.cat('DSP / Programmatic').join(', ')}.`)
            : info(
                'No DSP tags observed — display/programmatic may be inactive or served without on-site tagging.'
              )
      },
      {
        label: 'CTV / streaming measurement',
        evaluate: c =>
          c.has(/mntn|tatari/)
            ? good('CTV measurement platform detected — streaming ties back to site response.')
            : info(
                'No CTV measurement tags — streaming impact would rest on attribution/MMM instead.'
              )
      },
      {
        label: 'Ad-server / Floodlight tagging',
        evaluate: c =>
          c.has(/floodlight|dv360|cm360/)
            ? good('Floodlight present — impressions and conversions share an ad-server spine.')
            : info('No Floodlight — cross-channel dedup depends on platform-native counting.')
      },
      {
        label: 'Independent measurement (critical for TV)',
        evaluate: c =>
          c.cat('Attribution & Measurement').length
            ? good(
                `Attribution layer detected (${c.cat('Attribution & Measurement').join(', ')}) — upper-funnel spend can be defended.`
              )
            : warn(
                'No attribution/MMM platform — TV & audio results would be argued from anecdotes.'
              )
      },
      {
        label: 'Cookieless identity (1P data)',
        evaluate: c =>
          c.has(/liveramp|unified id|id5/)
            ? good(
                'Identity resolution detected — programmatic can target without third-party cookies.'
              )
            : info(
                'No identity-resolution tags — cookieless reach in programmatic will lean on platform IDs.'
              )
      },
      {
        label: 'Linear TV, audio & OOH',
        evaluate: () =>
          info(
            'Not visible client-side by nature — measured via MMM, spike analysis, and promo codes.'
          )
      }
    ]
  },
  {
    id: 'commerce',
    name: 'Commerce',
    blurb: 'Amazon · Commerce Media · Commerce Operations',
    types: ECOM_TYPES,
    checks: [
      {
        label: 'Amazon advertising signals',
        evaluate: c =>
          c.has(/amazon ads|amazon ad ping/) || c.host(/amazon-adsystem/)
            ? good('Amazon ad tags observed — DTC and marketplace media are connected.')
            : info(
                'No Amazon ad signals on-site — normal if marketplace media runs off-site; confirm Amazon Ads access.'
              )
      },
      {
        label: 'Product feed signals',
        evaluate: c =>
          c.has(/pmax|merchant/)
            ? good('Merchant/feed signals present — the digital shelf is wired for commerce media.')
            : warn('No feed signals — shoppable placements need a healthy feed first.')
      },
      {
        label: 'Commerce platform',
        evaluate: c =>
          c.cat('Platform / CMS').length
            ? good(`Detected: ${c.cat('Platform / CMS').join(', ')}.`)
            : info('No commerce platform signature — verify where checkout lives.')
      },
      {
        label: 'Ratings & reviews (digital shelf)',
        evaluate: c =>
          c.cat('Reviews / UGC / Loyalty').length
            ? good('Review infrastructure present — ratings can syndicate to retail placements.')
            : warn(
                'No reviews platform — star ratings, a top commerce conversion lever, are unmanaged.'
              )
      },
      {
        label: 'Retail media / in-store',
        evaluate: () =>
          info(
            "Retail media networks and in-store sales aren't visible client-side — connect retailer data during onboarding."
          )
      }
    ]
  },
  {
    id: 'affiliate',
    name: 'Affiliate',
    blurb: 'Partnerships & performance publishers',
    checks: [
      {
        label: 'Affiliate platform',
        evaluate: c =>
          c.cat('Affiliate / Partnership').length
            ? good(`Detected: ${c.cat('Affiliate / Partnership').join(', ')}.`)
            : miss('No affiliate platform detected — partner traffic is untracked and unmanaged.')
      },
      {
        label: 'Referral programs',
        evaluate: c =>
          c.has(/friendbuy|extole|mention me|talkable|referral/)
            ? good('Customer referral mechanics detected — advocacy has rails.')
            : info('No referral platform — customer-to-customer growth is unharnessed.')
      },
      {
        label: 'Attribution hygiene',
        evaluate: c =>
          c.cat('Attribution & Measurement').length
            ? good(
                'Independent attribution present — affiliate can be judged on incrementality, not last-click claims.'
              )
            : warn(
                "No independent attribution — affiliate's classic last-click inflation goes unchecked."
              )
      }
    ]
  },
  {
    id: 'emailsms',
    name: 'Email & SMS',
    blurb: 'Lifecycle & owned channels',
    checks: [
      {
        label: 'Email platform',
        evaluate: c =>
          c.cat('Email / SMS / Marketing Automation').length
            ? good(`Detected: ${c.cat('Email / SMS / Marketing Automation').join(', ')}.`)
            : miss("No ESP detected — the highest-ROI channel in retail isn't running.")
      },
      {
        label: 'SMS capability',
        evaluate: c =>
          c.has(/attentive|postscript|smsbump|sms/)
            ? good('SMS platform present — the fastest owned channel is live.')
            : warn('No SMS platform — replenishment and drops lack their best channel.')
      },
      {
        label: 'Audience data (CDP)',
        evaluate: c =>
          c.cat('CDP / Data Platforms').length
            ? good('CDP present — segmentation can go beyond opens and clicks.')
            : warn('No CDP — lifecycle segmentation is limited to what the ESP sees.')
      },
      {
        label: 'Consent management',
        evaluate: c =>
          c.cat('Consent Management').length
            ? good('Consent infrastructure present — list growth is compliant by design.')
            : warn('No CMP — capture practices need a compliance review.')
      },
      {
        label: 'Flow coverage',
        evaluate: () =>
          info(
            "Welcome, abandonment, and winback flows aren't visible in a scan — audit inside the ESP (see Access to request)."
          )
      }
    ]
  },
  {
    id: 'cro',
    name: 'CRO',
    blurb: 'Conversion Rate Optimization',
    checks: [
      {
        label: 'Testing / personalization platform',
        evaluate: c =>
          c.cat('A/B Testing & Personalization').length
            ? good(
                `Detected: ${c.cat('A/B Testing & Personalization').join(', ')} — hypotheses can become experiments.`
              )
            : miss('No testing platform — site changes ship as opinions, not experiments.')
      },
      {
        label: 'Behavior insight (session replay)',
        evaluate: c =>
          c.cat('Session Replay / UX Analytics').length
            ? good('Replay/UX analytics present — friction is observable.')
            : warn('No session replay — you can see drop-off but not why.')
      },
      {
        label: 'Voice of customer',
        evaluate: c =>
          c.cat('Surveys, Quizzes & VoC').length
            ? good('Survey/VoC tooling present — the "why" behind the numbers is being collected.')
            : warn('No survey tooling — customer intent is being inferred, never asked.')
      },
      {
        label: 'Site search & merchandising',
        evaluate: c =>
          c.cat('Site Search & Merchandising').length
            ? good(
                `Detected: ${c.cat('Site Search & Merchandising').join(', ')} — high-intent searchers get optimized results.`
              )
            : info('Native platform search only — a merchandising layer is a common CRO win.')
      }
    ]
  },
  {
    id: 'influencer',
    name: 'Influencer',
    blurb: 'Creator & influencer marketing',
    limited: true,
    checks: [
      {
        label: 'UGC / creator content platform',
        evaluate: c =>
          c.has(/stackla|pixlee|emplifi|tint|olapic/)
            ? good('Creator-content platform detected — UGC has a pipeline to the site.')
            : info('No creator-content platform visible.')
      },
      {
        label: 'Creator commerce rails',
        evaluate: c =>
          c.cat('Affiliate / Partnership').length
            ? good(
                'Affiliate infrastructure detected — creator codes/links can be tracked and paid.'
              )
            : info('No affiliate rails — creator programs would lack payout and tracking plumbing.')
      },
      {
        label: 'Social proof loop',
        evaluate: c =>
          c.cat('Reviews / UGC / Loyalty').length
            ? good('Review/UGC platform present — creator content can feed on-site proof.')
            : info('No UGC surface on-site.')
      },
      {
        label: 'Program visibility',
        evaluate: () =>
          info(
            'Influencer programs largely live off-site (briefs, gifting, platform deals) — a scan sees only the on-site rails above.'
          )
      }
    ]
  }
];
